import React from "react";
import { Link } from "react-router-dom";
import { BellRing, Check, Copy, Loader2, Lock, Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { shortDate } from "@/lib/format";
import { btnGhost, iconBtnClass } from "@/components/Brand";

/** Whether the client has opened the gallery, and when we last nudged them. */
export const OpenState = ({ project }) => {
  const { t } = useI18n();
  const last = project.reminders?.[project.reminders.length - 1];

  return (
    <p data-testid={`open-state-${project.project_id}`}
      className={`mt-3 font-mono-accent text-[10px] tracking-[0.16em] uppercase ${
        project.open_count ? "text-[#D4AF37]" : "text-[#707070]"
      }`}>
      {project.open_count
        ? `${t("opened_not_submitted")} · ${shortDate(project.last_opened_at)}`
        : t("never_opened")}
      {last ? ` · ${t("reminded")} ${shortDate(last.at)}` : ""}
    </p>
  );
};

/** Status badge, batch-select checkbox and delete. */
export const CardTopRow = ({ project: p, picked, locked, onPick, onDelete }) => {
  const { t } = useI18n();

  return (
    <div className="flex items-start justify-between gap-4">
      <span className={`font-mono-accent text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
        p.status === "submitted" ? "text-[#D4AF37] border-[#D4AF37]/40" : "text-[#A8A8A8] border-[#2A2A2A]"
      }`}>
        {p.status === "submitted" ? t("submitted") : t("awaiting_client")}
      </span>
      <button data-testid={`pick-project-${p.project_id}`} onClick={() => onPick(p.project_id)}
        aria-pressed={picked} aria-label={t("selected_count", { n: 1 })}
        className="w-11 h-11 -m-2 flex items-center justify-center">
        <span className={`w-6 h-6 border flex items-center justify-center transition-colors ${
          picked ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-[#2A2A2A] hover:border-[#D4AF37]"
        }`}>
          {picked && <Check size={14} strokeWidth={3} />}
        </span>
      </button>
      <button data-testid={`delete-project-${p.project_id}`} onClick={() => onDelete(p)}
        className={`${iconBtnClass} transition-colors sm:opacity-0 sm:group-hover:opacity-100 ${
          locked ? "text-[#3d3d3d]" : "text-[#5A5A5A] hover:text-[#8B0000]"
        }`}
        aria-label={t("delete_project")} title={locked ? t("delete_paid_only") : t("delete_project")}>
        {locked ? <Lock size={15} /> : <Trash2 size={15} />}
      </button>
    </div>
  );
};

/** Manage, remind and copy-link actions. */
export const CardActions = ({ project: p, reminding, onRemind, onCopyLink }) => {
  const { t } = useI18n();

  return (
    <div className="mt-6 flex gap-3">
      <Link data-testid={`open-project-${p.project_id}`} to={`/projects/${p.project_id}`}
        className={`${btnGhost} flex-1 py-2.5`}>
        {t("manage")}
      </Link>
      {p.status !== "submitted" && p.has_client_contact && (
        <button data-testid={`remind-client-${p.project_id}`} onClick={() => onRemind(p)}
          disabled={reminding}
          className="border border-[#2A2A2A] px-4 min-h-[44px] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center"
          aria-label={t("remind_client")} title={t("remind_client")}>
          {reminding ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
        </button>
      )}
      <button data-testid={`copy-link-${p.project_id}`} onClick={() => onCopyLink(p)}
        className="border border-[#2A2A2A] px-4 min-h-[44px] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
        aria-label="Copy client link">
        <Copy size={14} />
      </button>
    </div>
  );
};
